// Copyright 2025, University of Maryland, All Rights Reserved
import { useMemo } from 'react'

import { useKnowledgeBaseStore } from '../core/KnowledgeBaseProvider'
import type { KnowledgeBaseMode } from '../types'
import { resolveSettingValue } from '../utils/settings'

export interface KnowledgeBaseSettings {
  mode: KnowledgeBaseMode
  scoreLabel: string
  scoreExplanation: string
}

export function useKnowledgeBaseSettings(_config: unknown): KnowledgeBaseSettings {
  const store = useKnowledgeBaseStore()

  return useMemo(() => {
    const storeSettings = store.settings
    // Check if store has actual KB settings (not just config props like nodeInstanceId)
    const hasKBSettings = storeSettings && 'mode' in storeSettings
    const settings = hasKBSettings ? storeSettings : {}

    const rawMode = resolveSettingValue(settings, 'mode').trim()
    const mode: KnowledgeBaseMode = rawMode === 'report' ? 'report' : rawMode === 'criteria' ? 'criteria' : 'findings'

    // Findings mode uses "Risk" terminology; report mode doesn't use scores
    const scoreLabel = 'Risk'
    const scoreExplanation = 'The risk level (1 = low, 5 = critical).'

    return {
      mode,
      scoreLabel,
      scoreExplanation,
    }
  }, [store.settings])
}
